import { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ChatContext = createContext(null);

// Short, pleasant "new message" ping synthesized via Web Audio (no asset needed).
let _audioCtx = null;
export const playPing = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    _audioCtx = _audioCtx || new Ctx();
    if (_audioCtx.state === "suspended") _audioCtx.resume();
    const now = _audioCtx.currentTime;
    const notes = [880, 1174.7]; // A5 -> D6, a quick two-tone chime
    notes.forEach((freq, i) => {
      const osc = _audioCtx.createOscillator();
      const gain = _audioCtx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const t = now + i * 0.11;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.15, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
      osc.connect(gain).connect(_audioCtx.destination);
      osc.start(t);
      osc.stop(t + 0.2);
    });
  } catch { /* audio not available */ }
};

export const ChatProvider = ({ children }) => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [open, setOpen] = useState(false);
  const [typingPeer, setTypingPeer] = useState(null);
  const [soundOn, setSoundOn] = useState(typeof window !== "undefined" && localStorage.getItem("xm_chat_sound") !== "off");
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const activeIdRef = useRef(null);
  const openRef = useRef(false);
  const conversationsRef = useRef([]);
  const openConvRef = useRef(null);
  const soundRef = useRef(typeof window !== "undefined" && localStorage.getItem("xm_chat_sound") !== "off");
  activeIdRef.current = activeId;
  openRef.current = open;
  conversationsRef.current = conversations;

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/conversations");
      setConversations(data);
    } catch { /* not logged in */ }
  }, []);

  const upsertConversation = useCallback((conv) => {
    setConversations((prev) => {
      const rest = prev.filter((c) => c.conversation_id !== conv.conversation_id);
      return [conv, ...rest].sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
    });
  }, []);

  const connect = useCallback(async () => {
    if (!user) return;
    let ticket = "";
    try {
      const { data } = await api.get("/chat/ws-token");
      ticket = data?.token || "";
    } catch { /* fall back to cookie-based auth */ }
    if (!user) return; // user may have logged out during the await
    const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
    const url = `${base}/api/ws/chat${ticket ? `?token=${encodeURIComponent(ticket)}` : ""}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onmessage = (e) => {
      let data;
      try { data = JSON.parse(e.data); } catch { return; }
      if (data.type === "message") {
        const msg = data.message;
        if (data.conversation_id === activeIdRef.current) {
          setMessages((m) => (m.some((x) => x.message_id === msg.message_id) ? m : [...m, msg]));
          if (openRef.current) api.get(`/chat/conversations/${data.conversation_id}/messages`).catch(() => {});
        } else if (msg && user && msg.sender_id !== user.user_id) {
          // Incoming message for a conversation the user isn't currently viewing → alert them.
          const conv = conversationsRef.current.find((c) => c.conversation_id === data.conversation_id);
          const who = conv?.peer?.name || "New message";
          if (soundRef.current) playPing();
          toast(`💬 ${who}`, {
            description: (msg.text || "").slice(0, 90),
            action: { label: "Open", onClick: () => openConvRef.current?.(data.conversation_id) },
          });
        }
      } else if (data.type === "conversation") {
        const conv = data.conversation;
        if (conv.conversation_id === activeIdRef.current && openRef.current) conv.unread = 0;
        upsertConversation(conv);
      } else if (data.type === "typing") {
        if (data.conversation_id === activeIdRef.current) {
          setTypingPeer(data.user_id);
          setTimeout(() => setTypingPeer(null), 2500);
        }
      }
    };
    ws.onclose = () => {
      wsRef.current = null;
      if (user) reconnectRef.current = setTimeout(connect, 3000);
    };
  }, [user, upsertConversation]);

  useEffect(() => {
    if (user) {
      loadConversations();
      connect();
    } else {
      setConversations([]); setMessages([]); setActiveId(null); setOpen(false);
    }
    return () => {
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) { const w = wsRef.current; wsRef.current = null; w.close(); }
    };
  }, [user, connect, loadConversations]);

  const openConversation = useCallback(async (conversationId, popup = true) => {
    setActiveId(conversationId);
    setMessages([]);
    if (popup) setOpen(true);
    try {
      const { data } = await api.get(`/chat/conversations/${conversationId}/messages`);
      setMessages(data.messages);
      setConversations((prev) => prev.map((c) => (c.conversation_id === conversationId ? { ...c, unread: 0 } : c)));
    } catch { /* ignore */ }
  }, []);
  openConvRef.current = openConversation;

  const toggleSound = useCallback(() => {
    setSoundOn((v) => {
      const nv = !v;
      soundRef.current = nv;
      try { localStorage.setItem("xm_chat_sound", nv ? "on" : "off"); } catch { /* ignore */ }
      return nv;
    });
  }, []);

  const startConversation = useCallback(async (peerId, productId, popup = true) => {
    const { data } = await api.post("/chat/conversations", { peer_id: peerId, product_id: productId });
    upsertConversation({ ...data, unread: 0 });
    await openConversation(data.conversation_id, popup);
    return data;
  }, [openConversation, upsertConversation]);

  const sendMessage = useCallback(async (text) => {
    const clean = (text || "").trim();
    if (!clean || !activeIdRef.current) return;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "message", conversation_id: activeIdRef.current, text: clean }));
    } else {
      try {
        const { data } = await api.post(`/chat/conversations/${activeIdRef.current}/messages`, { text: clean });
        setMessages((m) => (m.some((x) => x.message_id === data.message_id) ? m : [...m, data]));
      } catch { /* ignore */ }
    }
  }, []);

  const sendTyping = useCallback(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN && activeIdRef.current) {
      ws.send(JSON.stringify({ type: "typing", conversation_id: activeIdRef.current }));
    }
  }, []);

  const unread = useMemo(
    () => conversations.reduce((s, c) => s + (open && c.conversation_id === activeId ? 0 : (c.unread || 0)), 0),
    [conversations, open, activeId]
  );

  const value = useMemo(() => ({
    conversations, messages, activeId, open, setOpen, unread, typingPeer,
    openConversation, startConversation, sendMessage, sendTyping, loadConversations,
    soundOn, toggleSound,
    closeThread: () => setActiveId(null),
  }), [conversations, messages, activeId, open, unread, typingPeer, openConversation, startConversation, sendMessage, sendTyping, loadConversations, soundOn, toggleSound]);

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => useContext(ChatContext);
